import React, { useEffect } from 'react';

import {
  AvatarPhotoEditor,
  Box,
  Button,
  FormCheckbox,
  FormField,
  Loader,
  PageHeader,
} from 'components';
import { useIsMobile, useNotifications } from 'hooks';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useTenant } from 'modules/tenant';

import { tenantSelectors } from 'selectors';

import { getTenantAvatarUrl } from 'utils/app';

import { APITenant } from 'types/api';

enum FormFields {
  Name = 'name',
  Description = 'description',
  IsSandbox = 'isSandbox',
}

type TenantFormData = {
  [FormFields.Name]: string;
  [FormFields.Description]: string;
  [FormFields.IsSandbox]: boolean;
};

type CreateEditProps = {
  onModalClose: () => void;
  tenantId?: string | null;
};

export const TenantCreateEditForm: React.FC<CreateEditProps> = ({
  onModalClose,
  tenantId,
}) => {
  const { loadTenant, clearTenant } = useTenant();
  const tenant = useSelector(tenantSelectors.tenant);
  const isEditMode = !!tenantId;

  useEffect(() => {
    if (tenantId) {
      loadTenant(tenantId);
    }
    return () => {
      clearTenant();
    };
  }, [tenantId, loadTenant, clearTenant]);

  if (isEditMode && !tenant) {
    return <Loader />;
  }

  return (
    <CreateEditForm
      tenant={tenant}
      isEditMode={isEditMode}
      onModalClose={onModalClose}
    />
  );
};

type Props = {
  tenant: APITenant | null;
  onModalClose: () => void;
  isEditMode: boolean;
};

export const CreateEditForm: React.FC<Props> = ({
  tenant,
  isEditMode,
  onModalClose,
}) => {
  const avatarEditorRef =
    React.useRef<React.ElementRef<typeof AvatarPhotoEditor>>(null);
  const { error, success } = useNotifications();
  const { updateTenant, createTenant } = useTenant();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<TenantFormData>({
    mode: 'onChange',
    defaultValues: {
      [FormFields.Name]: tenant?.name || '',
      [FormFields.Description]: tenant?.description || '',
      [FormFields.IsSandbox]: tenant?.isSandbox || false,
    },
  });

  const updateTenantPending = useSelector(
    tenantSelectors.updateTenantPending,
  );
  const createTenantPending = useSelector(
    tenantSelectors.createTenantPending,
  );

  const isFormPending = createTenantPending || updateTenantPending;

  const handleCancel = () => {
    onModalClose();
  };

  const onSubmit = async (formData: TenantFormData) => {
    const avatarFormData = await avatarEditorRef.current?.getAvatarImage();
    if (isEditMode && tenant) {
      const submitFormData = new FormData();

      if (avatarFormData) {
        const fileEntry = Array.from(avatarFormData.entries())[0];
        if (fileEntry && fileEntry[1] instanceof Blob) {
          submitFormData.append('file', fileEntry[1], 'profile-picture.png');
        }
      }

      submitFormData.append('name', formData.name);
      submitFormData.append('description', formData.description || '');
      submitFormData.append('id', tenant.id);

      const result = await updateTenant(tenant.id, submitFormData);

      if (result.error) {
        error(result.payload.body?.title || t('tenantForm.edit.error'));
      } else {
        success(t('tenantForm.edit.sucscefull'));
        onModalClose();
      }
      return;
    }

    const submitFormData = new FormData();

    if (avatarFormData) {
      const fileEntry = Array.from(avatarFormData.entries())[0];
      if (fileEntry && fileEntry[1] instanceof Blob) {
        submitFormData.append('file', fileEntry[1], 'profile-picture.png');
      }
    }

    submitFormData.append('name', formData.name);
    submitFormData.append('description', formData.description || '');
    submitFormData.append('isSandbox', formData.isSandbox ? 'true' : 'false');

    const result = await createTenant(submitFormData);

    if (result.error) {
      error(result.payload.body?.title || t('tenantForm.create.error'));
    } else {
      success(t('tenantForm.create.sucscefull'));
      onModalClose();
    }
  };

  return (
    <Box padding="1rem  1rem  0 1rem">
      <PageHeader
        header={
          isEditMode
            ? t('tenantForm.edit.header')
            : t('tenantForm.create.header')
        }
        subHeader={
          isEditMode
            ? t('tenantForm.edit.subHeader')
            : t('tenantForm.create.subHeader')
        }
      >
        <Box
          display="flex"
          flexWrap={isMobile ? 'wrap' : 'nowrap'}
          columnGap="1rem"
        >
          <Button
            type="submit"
            variant="outlined"
            disabled={isFormPending}
            onClick={handleSubmit(onSubmit)}
            size="medium"
            color="primary"
          >
            {t('common.form.save')}
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={handleCancel}
            size="medium"
            color="error"
          >
            {t('common.form.cancel')}
          </Button>
        </Box>
      </PageHeader>
      <Box
        display="flex"
        flexWrap={isMobile ? 'wrap' : 'nowrap'}
        flexDirection="row"
        gap="2rem"
      >
        <Box sx={{ padding: '1rem' }}>
          <AvatarPhotoEditor
            ref={avatarEditorRef}
            //@ts-ignore
            avatarUrl={getTenantAvatarUrl(tenant)}
            showSaveButton={false}
          />
        </Box>
        <form>
          <FormField
            fieldName={FormFields.Name}
            fieldError={errors[FormFields.Name] as FieldError}
            label={t('tenantList.column.name')}
            placeholder={
              t('common.form.enterValueFor') + t('tenantList.column.name')
            }
            requiredErrorMessage={t('common.form.fieldRequired')}
            control={control}
            maxLength={150}
            isRequired
            isDisabled={isFormPending}
          />
          <FormField
            fieldName={FormFields.Description}
            fieldError={errors[FormFields.Description] as FieldError}
            label={t('tenantList.column.description')}
            placeholder={
              t('common.form.enterValueFor') +
              t('tenantList.column.description')
            }
            control={control}
            maxLength={250}
            isRequired={false}
            isDisabled={isFormPending}
          />
          <FormCheckbox
            fieldName={FormFields.IsSandbox}
            fieldError={errors[FormFields.IsSandbox] as FieldError}
            label={t('tenantForm.sandbox.label')}
            title={t('tenantForm.sandbox.placeholder')}
            control={control}
            isRequired={false}
            isDisabled={isFormPending || isEditMode}
          />
        </form>
      </Box>
    </Box>
  );
};
